import { useCallback, useMemo, useState } from 'react';
import { Trans } from '@lingui/react/macro';
import TableViewLayout from '@duke-hq/ui/components/TableViewLayout/TableViewLayout';
import { trpcAdmin } from '@duke-hq/app-admin-web/lib/trpcAdmin';
import { DataTable } from '@duke-hq/ui/components/DataTable/index';
import { DataTableCellAction, DataTableSchema } from '@duke-hq/ui/components/DataTable/schema';
import { ScheduledTaskRuns } from './ScheduledTaskRuns';
import { useNavigate, useParams } from 'react-router-dom';
import { PageHeading } from '@duke-hq/ui/components/PageHeading';
import { SortOrder } from '@duke-hq/lib/pagination';
import { withPermission } from '../withPermission';

export const ScheduledTasksPage = withPermission('canAccessDeveloperTools', () => {
  return <ScheduledTasksTable />;
});

export function ScheduledTasksTable() {
  const [sortOrder, setSortOrder] = useState<{
    [key: string]: SortOrder | undefined;
  }>({});

  const { selectedTaskName } = useParams();
  const navigate = useNavigate();

  const onCloseDrawer = useCallback(() => {
    navigate(`/scheduled-tasks/`);
  }, [navigate]);

  const { data: scheduledTasksData } = trpcAdmin.scheduledTasks.list.useQuery({
    table: {
      sortOrder,
    },
  });

  const onSortChange = useCallback(
    (columnName: string, direction?: SortOrder) =>
      setSortOrder((prev) => {
        if (direction === undefined) {
          const newSortOrder = { ...prev };
          delete newSortOrder[columnName];
          return newSortOrder;
        } else {
          return { ...prev, [columnName]: direction };
        }
      }),
    [setSortOrder],
  );

  const handleAction = useCallback(
    async (
      action: DataTableCellAction<{
        taskName: string;
      }>,
    ) => {
      switch (action.kind) {
        case 'view.runs': {
          if (
            action.data !== null &&
            'taskName' in action.data &&
            typeof action.data['taskName'] === 'string'
          ) {
            navigate(`/scheduled-tasks/${encodeURIComponent(action.data.taskName)}`);
          }
          break;
        }
      }
    },
    [navigate],
  );

  const data = useMemo(() => {
    if (typeof scheduledTasksData === 'undefined') {
      return;
    }

    return {
      headers: {
        ...scheduledTasksData?.headers,
      },
      rows: scheduledTasksData?.rows.map((row) => ({
        id: row.id,
        cells: {
          ...row.cells,
        },
      })),
    } satisfies DataTableSchema;
  }, [scheduledTasksData]);

  if (typeof scheduledTasksData === 'undefined' || typeof data === 'undefined') {
    return <div>Loading...</div>;
  }

  return (
    <>
      <TableViewLayout
        pageHeader={[
          <PageHeading key="scheduled-tasks">
            <Trans>Scheduled Tasks</Trans>
          </PageHeading>,
        ]}
        filters={null}
        tableActionsLeft={null}
        table={
          <DataTable
            data={data}
            onSortChange={onSortChange}
            handleAction={handleAction}
            tableProps={{
              colorScheme: 'purple',
              size: 'sm',
            }}
          />
        }
        pagination={null}
      />
      <ScheduledTaskRuns
        taskName={selectedTaskName}
        isOpen={typeof selectedTaskName === 'string'}
        onClose={onCloseDrawer}
      />
    </>
  );
}
